/**
 * TaskService
 *
 * This service handles all task-related operations in the application.
 * It provides methods for managing tasks, including creation, retrieval,
 * updating, and status management. The service supports operations for both
 * individual tasks and system-wide task management, with filtering capabilities
 * by employee, matter, and status.
 *
 * @class
 */
import {
  pathAddTask,
  pathAllTasks,
  pathMyTasksEmployeeId,
  pathTaskByMatterId,
  pathTaskStatusDistribution,
  pathTaskStatusDistributionMatterId,
  pathTaskCompleteTaskId,
  pathTaskInProgressTaskId,
  pathTaskPendingTaskId,
  pathTasksByMatterEmployeeMatterIdEmployeeId,
  pathTasksByMonthMatterIdMonth,
  pathRecentActivities,
  pathUpdateTaskById,
} from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import {
  AddTaskRequest,
  AddTaskResponse,
  DeleteTaskResponse,
  GetRecentActivities,
  GetTasksByEmployeeResponse,
  GetTasksByMatterEmployeeMatterIdEmployeeIdResponse,
  GetTasksByMatterIdResponse,
  GetTasksByMonthMatterIdMonthResponse,
  GetTasksResponse,
  GetTaskStatusDistributionResponse,
  TaskApiResponse,
  UpdateTaskRequest,
  UpdateTaskResponse,
  UpdateTaskStatusResponse,
} from '@frontend/Models/Tasks/TaskRequestResponseModels';

const http = new HttpService();

class TaskService {
  /**
   * Retrieves the distribution of task statuses across all tasks
   * @returns {Promise<GetTaskStatusDistributionResponse>} Array of task status distribution objects
   */
  static async getTaskStatusDistribution(): Promise<GetTaskStatusDistributionResponse> {
    const response = await http.get<TaskApiResponse<GetTaskStatusDistributionResponse>>(
      pathTaskStatusDistribution
    );
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching task status distribution.');
    }
    return response.data;
  }

  /**
   * Retrieves the distribution of task statuses for a specific matter
   * @param matterId the ID of the matter
   * @returns {Promise<GetTaskStatusDistributionResponse>} Array of task status distribution objects for the matter
   */
  static async getTaskStatusDistributionByMatterId(
    matterId: string
  ): Promise<GetTaskStatusDistributionResponse> {
    const response = await http.get<TaskApiResponse<GetTaskStatusDistributionResponse>>(
      pathTaskStatusDistributionMatterId(matterId)
    );
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching task status distribution.');
    }
    return response.data;
  }

  /**
   * Retrieves all tasks assigned to a specific employee
   * @param employeeId the ID of the employee
   * @returns {Promise<GetTasksByEmployeeResponse>} Array of tasks assigned to the employee
   */
  static async getTasksByEmployeeId(
    employeeId: string
  ): Promise<GetTasksByEmployeeResponse> {
    const response = await http.get<TaskApiResponse<GetTasksByEmployeeResponse>>(
      pathMyTasksEmployeeId(employeeId)
    );
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching tasks by employee.');
    }
    return response.data;
  }

  /**
   * Retrieves all tasks from the system
   *
   * @returns {Promise<GetTasksResponse>} Array of all tasks
   * @throws {Error} If the API request fails
   */
  static async getAllTasks(): Promise<GetTasksResponse> {
    const response = await http.get<TaskApiResponse<GetTasksResponse>>(pathAllTasks);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching all tasks.');
    }
    return response.data;
  }

  /**
   * This method adds a new task to the system.
   * @param taskData - Data for the new task to be added
   * @returns {Promise<AddTaskResponse>} Response from the API after adding the task
   */
  static async addTask(taskData: AddTaskRequest): Promise<AddTaskResponse> {
    const response = await http.post<TaskApiResponse<AddTaskResponse>>(pathAddTask, taskData);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while adding the task.');
    }
    return response.data;
  }

  /**
   * Retrieves all tasks associated with a specific matter
   *
   * @param matterId the matter ID
   * @returns
   */
  static async getTasksByMatterId(
    matterId: string
  ): Promise<GetTasksByMatterIdResponse> {
    const response = await http.get<TaskApiResponse<GetTasksByMatterIdResponse>>(pathTaskByMatterId(matterId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching tasks by matter.');
    }
    return response.data;
  }

  /**
   * Retrieves tasks associated with both a specific matter and employee
   *
   * @param {string} matterId - ID of the matter
   * @param {string} employeeId - ID of the employee
   * @returns {Promise<GetTasksByMatterEmployeeMatterIdEmployeeIdResponse>} Array of tasks matching both criteria
   * @throws {Error} If the API request fails
   */
  static async getTasksByMatterAndEmployee(
    matterId: string,
    employeeId: string
  ): Promise<GetTasksByMatterEmployeeMatterIdEmployeeIdResponse> {
    const response = await http.get<TaskApiResponse<GetTasksByMatterEmployeeMatterIdEmployeeIdResponse>>(
      pathTasksByMatterEmployeeMatterIdEmployeeId(matterId, employeeId)
    );
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching tasks by matter and employee.');
    }
    return response.data;
  }

  /**
   * Updates a task's status to "Pending"
   *
   * @param taskId the ID of the task to update
   * @returns
   */
  static async updateTaskStatusToPending(
    taskId: string
  ): Promise<UpdateTaskStatusResponse> {
    const response = await http.put<TaskApiResponse<UpdateTaskStatusResponse>>(pathTaskPendingTaskId(taskId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating task status.');
    }
    return response.data;
  }

  /**
   * updates a task's status to "In Progress"
   * @param taskId the ID of the task to update
   * @returns
   */
  static async updateTaskStatusToInProgress(
    taskId: string
  ): Promise<UpdateTaskStatusResponse> {
    const response = await http.put<TaskApiResponse<UpdateTaskStatusResponse>>(pathTaskInProgressTaskId(taskId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating task status.');
    }
    return response.data;
  }

  /**
   * Updates a task's status to "Complete"
   * @param taskId the ID of the task to update
   * @returns
   */
  static async updateTaskStatusToComplete(
    taskId: string
  ): Promise<UpdateTaskStatusResponse> {
    const response = await http.put<TaskApiResponse<UpdateTaskStatusResponse>>(pathTaskCompleteTaskId(taskId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating task status.');
    }
    return response.data;
  }

  /**
   * Retrieves tasks for a specific matter in a given month
   *
   * @param {string} matterId - ID of the matter
   * @param {string} month - Month to filter tasks by
   * @returns {Promise<Array>} Array of tasks for the specified month
   * @throws {Error} If the API request fails
   */
  static async getTasksByMonth(
    matterId: string,
    month: string
  ): Promise<GetTasksByMonthMatterIdMonthResponse> {
    const response = await http.get<TaskApiResponse<GetTasksByMonthMatterIdMonthResponse>>(
      pathTasksByMonthMatterIdMonth(matterId, month)
    );
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching tasks by month.');
    }
    return response.data;
  }

  /**
   * Retrieves tasks with the closest due dates
   *
   * @returns {Promise<GetRecentActivities>} Array of tasks sorted by due date
   * @throws {Error} If the API request fails
   */
  static async getClosestDueDates(): Promise<GetRecentActivities> {
    const response = await http.get<TaskApiResponse<GetRecentActivities>>(pathRecentActivities);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while fetching recent activities.');
    }
    return response.data;
  }

  /**
   * Updates a task with the provided data.
   * Only include fields in taskData that you want to update.
   * Uses the endpoint: PUT /tasks/<task_id>
   * @param taskId the ID of the task to update
   * @param taskData the fields to update (all fields optional)
   * @returns {Promise<UpdateTaskResponse>} Response from the API after updating the task
   */
  static async updateTask(
    taskId: string,
    taskData: UpdateTaskRequest
  ): Promise<UpdateTaskResponse> {
    const response = await http.put<TaskApiResponse<UpdateTaskResponse>>(pathUpdateTaskById(taskId), taskData);
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while updating the task.');
    }
    return response.data;
  }

  /**
   * This method deletes a task by its ID.
   * Uses the existing endpoint: DELETE /tasks/<task_id>
   * @param taskId the ID of the task to delete
   * @returns
   */
  static async deleteTask(taskId: string): Promise<DeleteTaskResponse> {
    const response = await http.delete<TaskApiResponse<DeleteTaskResponse>>(pathUpdateTaskById(taskId));
    if (!response.successful || !response.data) {
      throw new Error(response.error || 'An error occurred while deleting the task.');
    }
    return response.data;
  }
}

export default TaskService;
